"use client";
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Link,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { useRouter } from "next/navigation";
import NextLink from "next/link";
import Image from "next/image";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { Plus_Jakarta_Sans } from "next/font/google";
import { useTranslation } from "react-i18next";

const PlusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

const AIAgentFooter = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [emailVerifying, setEmailVerifying] = useState(false);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const router = useRouter();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (!email) {
      setEmailError("");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError(t("footer.emailError"));
    } else {
      setEmailError("");
    }
  }, [email, t]);
  const verifyEmail = async (email) => {
    try {
      setEmailVerifying(true);
      const res = await fetch("/api/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      return await res.json();
    } catch {
      return {
        valid: false,
        message: "Email verification failed. Please try again.",
      };
    } finally {
      setEmailVerifying(false);
    }
  };
  const handleSubmit = async () => {
    if (!email) {
      setEmailError(t("footer.emailRequired"));
      return;
    }
    if (!validateEmail(email)) {
      setEmailError(t("footer.emailError"));
      return;
    }

    // 🔹 NAYA STEP — real email verify
    const verifyResult = await verifyEmail(email.trim());
    if (!verifyResult.valid) {
      setEmailError(verifyResult.message || t("footer.emailError"));
      return;
    }

    try {
      const response = await fetch("/api/save-blog-newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      setEmail("");
      setEmailError("");
      setSubscriptionSuccess(true);
      setTimeout(() => setSubscriptionSuccess(false), 5000);
    } catch (error) {
      setEmailError(t("footer.emailFailed"));
    }
  };

  return (
    <Box
      direction={isArabic ? "rtl" : "ltr"}
      sx={{ backgroundColor: "#EEEAFF" }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 3, sm: 4, md: 4.9 }}
          sx={{
            pt: { xs: "8%", md: "6%" },
            direction: isArabic ? "rtl" : "ltr",
          }}
        >
          {/* Column 1 — Logo + Description */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ marginBottom: "16px" }}>
              <Image
                src="https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/getgeek_new_logo_2x.webp"
                alt="Contact page footer for AI recruiting tool demo request"
                width={140}
                height={40}
                style={{ objectFit: "contain" }}
                priority
              />
            </Box>
            <Typography
              sx={{
                fontFamily: PlusJakartaSans.style.fontFamily,
                fontSize: "13px",
                color: "#6b7280",
                lineHeight: 1.7,
                textAlign: isArabic ? "right" : "left",
                maxWidth: "210px",
              }}
            >
              {t("footer.companyDesc")}
            </Typography>
          </Grid>

          {/* Column 2 — Help */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: PlusJakartaSans.style.fontFamily,
                fontWeight: "bold",
                color: "#0F172A",
                fontSize: { xs: "11px", md: "12px" },
                letterSpacing: "0.5px",
                mb: { xs: 2, md: 3 },
                textTransform: "uppercase",
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("footer.help")}
            </Typography>
            <Stack spacing={{ xs: 1.5, md: 2 }}>
              <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
                <Link
                  underline="none"
                  sx={{
                    fontFamily: PlusJakartaSans.style.fontFamily,
                    fontWeight: "500",
                    fontSize: { xs: "13px", md: "14px" },
                    color: "#6B7280",
                    cursor: "pointer",
                    textAlign: isArabic ? "right" : "left",
                    display: "block",
                    "&:hover": { color: "#8b5cf6" },
                  }}
                >
                  {t("footer.customerSupport")}
                </Link>
              </NextLink>
            </Stack>
          </Grid>

          {/* Column 3 — Platforms */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: PlusJakartaSans.style.fontFamily,
                fontWeight: "bold",
                color: "#0F172A",
                fontSize: { xs: "11px", md: "12px" },
                letterSpacing: "0.5px",
                mb: { xs: 2, md: 3 },
                textTransform: "uppercase",
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("footer.platforms")}
            </Typography>
            <Link
              href="https://www.candidate-portal.getgeek.ai/"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                fontFamily: PlusJakartaSans.style.fontFamily,
                fontWeight: "500",
                fontSize: { xs: "13px", md: "14px" },
                color: "#6B7280",
                cursor: "pointer",
                textAlign: isArabic ? "right" : "left",
                display: "block",
                mb: "17px",
                "&:hover": { color: "#8b5cf6" },
              }}
            >
              {t("footer.jobBoard")}
            </Link>
          </Grid>

          {/* Column 4 — Resources */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: PlusJakartaSans.style.fontFamily,
                fontWeight: "bold",
                color: "#0F172A",
                fontSize: { xs: "11px", md: "12px" },
                letterSpacing: "0.5px",
                mb: { xs: 2, md: 3 },
                textTransform: "uppercase",
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("footer.resources")}
            </Typography>
            <NextLink href="/blog" passHref legacyBehavior>
              <Link
                underline="none"
                sx={{
                  fontFamily: PlusJakartaSans.style.fontFamily,
                  fontWeight: "500",
                  fontSize: { xs: "13px", md: "14px" },
                  color: "#6B7280",
                  cursor: "pointer",
                  textAlign: isArabic ? "right" : "left",
                  display: "block",
                  mb: "17px",
                  "&:hover": { color: "#8b5cf6" },
                }}
              >
                {t("footer.blogs")}
              </Link>
            </NextLink>
            <NextLink href="/faq-about-ai-recruiting-software" passHref legacyBehavior>
              <Link
                underline="none"
                sx={{
                  fontFamily: PlusJakartaSans.style.fontFamily,
                  fontWeight: "500",
                  fontSize: { xs: "13px", md: "14px" },
                  color: "#6B7280",
                  cursor: "pointer",
                  textAlign: isArabic ? "right" : "left",
                  display: "block",
                  mb: "17px",
                  "&:hover": { color: "#8b5cf6" },
                }}
              >
                {t("footer.faqs")}
              </Link>
            </NextLink>
          </Grid>

          {/* Column 5 — More */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: PlusJakartaSans.style.fontFamily,
                fontWeight: "bold",
                color: "#0F172A",
                fontSize: { xs: "11px", md: "12px" },
                letterSpacing: "0.5px",
                mb: { xs: 2, md: 3 },
                textTransform: "uppercase",
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("footer.more")}
            </Typography>
            {[
              { label: t("footer.aiRecruiter"), path: "/zayn-ai-recruiter" },
              {
                label: t("footer.aiAgents"),
                path: "/ai-agents",
              },
              {
                label: t("footer.features"),
                path: "/features",
              },
              {
                label: t("footer.pricing"),
                path: "/ai-driven-recruitment-platform-pricing",
              },
            ].map((item) => (
              <NextLink key={item.path} href={item.path} passHref legacyBehavior>
                <Link
                  underline="none"
                  sx={{
                    fontFamily: PlusJakartaSans.style.fontFamily,
                    fontWeight: "500",
                    fontSize: { xs: "13px", md: "14px" },
                    color: "#6B7280",
                    cursor: "pointer",
                    textAlign: isArabic ? "right" : "left",
                    display: "block",
                    mb: "17px",
                    "&:hover": { color: "#8b5cf6" },
                  }}
                >
                  {item.label}
                </Link>
              </NextLink>
            ))}
          </Grid>

          {/* Column 6 — Newsletter */}
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: PlusJakartaSans.style.fontFamily,
                  fontWeight: "bold",
                  color: "#0F172A",
                  fontSize: { xs: "11px", md: "12px" },
                  letterSpacing: "0.5px",
                  mb: 2,
                  textTransform: "uppercase",
                  width: "100%",
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {t("footer.subscribeNewsletter")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                  overflow: "hidden",
                  width: "100%",
                  height: { xs: "44px", md: "48px" },
                  mb: 2,
                  flexDirection: isArabic ? "row-reverse" : "row",
                }}
              >
                <TextField
                  placeholder={t("footer.emailPlaceholder")}
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  inputProps={{
                    style: { textAlign: isArabic ? "right" : "left" },
                  }}
                  sx={{
                    flexGrow: 1,
                    "& .MuiOutlinedInput-root": {
                      height: "100%",
                      paddingRight: 0,
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: { xs: "0 12px", md: "0 16px" },
                      fontFamily: PlusJakartaSans.style.fontFamily,
                      fontSize: { xs: "13px", md: "14px" },
                      height: "100%",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: "#8B5CF6",
                    textTransform: "none",
                    px: { xs: 3, md: 2 },
                    height: "100%",
                    borderRadius: 0,
                    "&:hover": { bgcolor: "#7C3AED" },
                    fontFamily: PlusJakartaSans.style.fontFamily,
                    fontWeight: 600,
                    fontSize: { xs: "13px", md: "14px" },
                  }}
                >
                  {emailVerifying
                    ? t("footer.verifying")
                    : t("footer.join")}{" "}
                </Button>
              </Box>

              {/* Error/Success Messages */}
              <Box
                sx={{
                  minHeight: { xs: "34px", md: "22px" },
                  mb: 2,
                  maxWidth: { xs: "100%", md: "320px" }, // 🔹 input+button jitni width
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {emailError && !subscriptionSuccess && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "var(--font-plusjakarta)",
                      fontSize: { xs: "11px", md: "12px" },
                      lineHeight: 1.4,
                      color: "#EF4444",
                      wordBreak: "break-word",
                    }}
                  >
                    {emailError}
                  </Typography>
                )}
                {subscriptionSuccess && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "var(--font-plusjakarta)",
                      fontSize: { xs: "12px", md: "13px" },
                      color: "#10B981",
                    }}
                  >
                    {t("footer.subscribeSuccess")}
                  </Typography>
                )}
              </Box>

              {/* Contact Info */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 4, sm: 6, md: 4 },
                  mt: 4,
                  flexDirection: {
                    xs: "column",
                    sm: isArabic ? "row-reverse" : "row",
                  },
                  width: "100%",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: PlusJakartaSans.style.fontFamily,
                      fontWeight: "600",
                      fontSize: { xs: "10px", md: "11px" },
                      letterSpacing: "0.5px",
                      color: "#6B7280",
                      mb: 0.5,
                      textTransform: "uppercase",
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("footer.callUs")}
                  </Typography>
                  <Typography
                    dir="ltr"
                    sx={{
                      fontFamily: PlusJakartaSans.style.fontFamily,
                      fontWeight: "600",
                      fontSize: { xs: "14px", md: "15px" },
                      color: "#111827",
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    (+92) 323-0295713
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: PlusJakartaSans.style.fontFamily,
                      fontWeight: "600",
                      fontSize: { xs: "10px", md: "11px" },
                      letterSpacing: "0.5px",
                      color: "#6B7280",
                      mb: 0.5,
                      textTransform: "uppercase",
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("footer.emailUs")}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: PlusJakartaSans.style.fontFamily,
                      fontWeight: "600",
                      fontSize: { xs: "14px", md: "15px" },
                      color: "#111827",
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    <span
                      onClick={() =>
                        (window.location.href = "mailto:contact@getgeek.ai")
                      }
                      style={{ cursor: "pointer" }}
                    >
                      contact&#64;getgeek&#46;ai
                    </span>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: isArabic ? "row-reverse" : "row",
            },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 3 },
            pb: { xs: "4%", md: "1.5%" },
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "400",
              fontSize: { xs: "13px", md: "14px" },
              color: "#6B7280",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            {t("footer.copyright")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <NextLink href="/privacy-policy" passHref legacyBehavior>
                <Link
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#6b7280",
                    textDecoration: "underline",
                    textDecorationColor: "#c4b5fd",
                    textUnderlineOffset: "3px",
                    fontFamily: "var(--font-outfit)",
                    cursor: "pointer",
                    transition: "color 0.2s",
                    "&:hover": {
                      color: "#9628DF",
                      textDecorationColor: "#9628DF",
                    },
                  }}
                >
                  {t("privacyPolicy")}
                </Link>
              </NextLink>
              <Typography
                sx={{ fontSize: "14px", color: "#d1d5db", fontWeight: 400 }}
              >
                |
              </Typography>
              <NextLink href="/terms-and-conditions" passHref legacyBehavior>
                <Link
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#6b7280",
                    textDecoration: "underline",
                    textDecorationColor: "#c4b5fd",
                    textUnderlineOffset: "3px",
                    fontFamily: "var(--font-outfit)",
                    cursor: "pointer",
                    transition: "color 0.2s",
                    "&:hover": {
                      color: "#9628DF",
                      textDecorationColor: "#9628DF",
                    },
                  }}
                >
                  {t("termsConditions")}
                </Link>
              </NextLink>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.5, sm: 1 },
              flexWrap: "wrap",
              justifyContent: {
                xs: isArabic ? "flex-end" : "flex-start",
                sm: "center",
              },
              flexDirection: isArabic ? "row-reverse" : "row",
            }}
          >
            <IconButton
              component="a"
              href="https://twitter.com/Getgeekai"
              target="_blank"
              sx={{
                width: { xs: 32, md: 36 },
                height: { xs: 32, md: 36 },
                color: "#374151",
                transition: "color 0.2s ease",
                "&:hover": { color: "#8b5cf6" },
              }}
            >
              <TwitterIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
            </IconButton>
            <IconButton
              component="a"
              href="https://www.facebook.com/Getgeekai/"
              target="_blank"
              sx={{
                width: { xs: 32, md: 36 },
                height: { xs: 32, md: 36 },
                color: "#374151",
                transition: "color 0.2s ease",
                "&:hover": { color: "#8b5cf6" },
              }}
            >
              <FacebookIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
            </IconButton>
            <IconButton
              component="a"
              href="https://www.instagram.com/getgeek.ai/"
              target="_blank"
              sx={{
                width: { xs: 32, md: 36 },
                height: { xs: 32, md: 36 },
                color: "#374151",
                transition: "color 0.2s ease",
                "&:hover": { color: "#8b5cf6" },
              }}
            >
              <InstagramIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
            </IconButton>
            <IconButton
              component="a"
              href="https://www.youtube.com/@getgeekai"
              target="_blank"
              sx={{
                width: { xs: 32, md: 36 },
                height: { xs: 32, md: 36 },
                color: "#374151",
                transition: "color 0.2s ease",
                "&:hover": { color: "#8b5cf6" },
              }}
            >
              <YouTubeIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
            </IconButton>
            <IconButton
              component="a"
              href="https://www.linkedin.com/company/getgeek.ai/posts/?feedView=all"
              target="_blank"
              sx={{
                width: { xs: 32, md: 36 },
                height: { xs: 32, md: 36 },
                color: "#374151",
                transition: "color 0.2s ease",
                "&:hover": { color: "#8b5cf6" },
              }}
            >
              <LinkedInIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AIAgentFooter;
